/**
 * The app's own translations — no third-party service. Replaces Weglot (N78).
 *
 * English text is the key: `t('Funded Balance')` looks the string up in the
 * chosen language's dictionary and falls back to the English itself, so a
 * string nobody has translated yet still reads correctly, and a value the API
 * sends ("Active", "Pending") is translated whenever the dictionary knows it.
 *
 * Dictionaries live in `src/locales/<lang>/<area>.json`, one file per area of
 * the app (layout, dashboard, affiliate, …), each a flat
 * `{ "English text": "Translation" }` map. The `en` files are the master
 * list — every string the app shows, mapped to itself — and the place to
 * change the English wording without touching code.
 *
 * Placeholders: `t('Across {{count}} accounts', { count: 3 })`.
 *
 * `t` is a plain function, not a hook, so it works in render code, event
 * handlers and toasts alike. Switching language re-mounts the app (see
 * I18nProvider), so every `t()` in render runs again. The one rule: never call
 * `t()` at a module's top level — that runs once, in English. Keep English in
 * module constants and translate where they are rendered.
 */

export const LANGUAGES = [
  { code: 'en', native: 'English', english: 'English', flag: 'gb' },
  { code: 'fr', native: 'Français', english: 'French', flag: 'fr' },
  { code: 'de', native: 'Deutsch', english: 'German', flag: 'de' },
  { code: 'es', native: 'Español', english: 'Spanish', flag: 'es' },
  { code: 'cs', native: 'Čeština', english: 'Czech', flag: 'cz' },
  { code: 'pt', native: 'Português', english: 'Portuguese', flag: 'pt' },
];

export const DEFAULT_LANGUAGE = 'en';
const STORAGE_KEY = 'ac.language';

const isLanguage = (code) => LANGUAGES.some((l) => l.code === code);

/**
 * Every locale file, fetched on demand: a reader downloads only the language
 * they pick. Webpack splits each file into its own chunk. `require.context` is
 * webpack's and has to be called as written to be compiled; Jest has no such
 * thing, so tests (NODE_ENV=test) run in English.
 */
const locales =
  process.env.NODE_ENV === 'test'
    ? null
    : require.context('../locales', true, /\.json$/, 'lazy');

let current = DEFAULT_LANGUAGE;
let dictionary = {};
let reverse = {};

/** One language's areas, merged into a single lookup. */
export const loadLanguage = async (code) => {
  if (!locales) return {};
  const files = locales.keys().filter((key) => key.startsWith(`./${code}/`));
  const parts = await Promise.all(files.map((key) => locales(key)));
  return Object.assign({}, ...parts.map((mod) => mod?.default ?? mod));
};

export const setDictionary = (code, next) => {
  current = code;
  dictionary = next ?? {};
  reverse = {};
  Object.entries(dictionary).forEach(([english, translated]) => {
    if (typeof translated === 'string' && !(translated in reverse)) reverse[translated] = english;
  });
};

export const currentLanguage = () => current;

const fill = (text, vars) =>
  vars ? text.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => (vars[name] ?? match)) : text;

/** The translation of `text` in the current language, or `text` itself. */
export const t = (text, vars) => {
  if (typeof text !== 'string') return text;
  const hit = dictionary[text];
  return fill(typeof hit === 'string' && hit ? hit : text, vars);
};

/**
 * The English a translated string came from — for code that decides something
 * from displayed text (a status chip's colour from its label) and may be
 * handed the label already translated. Unknown text comes back unchanged.
 */
export const toEnglish = (text) =>
  typeof text === 'string' && text in reverse ? reverse[text] : text;

/** The reader's last choice — wrapped, as storage can be blocked or empty. */
export const storedLanguage = () => {
  try {
    const code = localStorage.getItem(STORAGE_KEY);
    return isLanguage(code) ? code : null;
  } catch {
    return null;
  }
};

export const storeLanguage = (code) => {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Storage blocked: the choice lasts until the tab closes.
  }
};
